import { Check, Info, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/contexts/LanguageContext";

interface TierConfig {
  id: "starter" | "growth" | "unlimited";
  isRecommended?: boolean;
  features: Array<{
    labelKey: string;
    quantityKey?: string;
  }>;
}

const TIER_CONFIGS: TierConfig[] = [
  {
    id: "starter",
    features: [
      { labelKey: "orderManagement" },
      { labelKey: "customerTracking" },
      { labelKey: "deliveryLink", quantityKey: "one" },
      { labelKey: "intakeForm", quantityKey: "one" },
      { labelKey: "deliveryFlow" },
      { labelKey: "logsHistory" },
    ],
  },
  {
    id: "growth",
    isRecommended: true,
    features: [
      { labelKey: "everything" },
      { labelKey: "deliveryLinks", quantityKey: "upToFive" },
      { labelKey: "intakeForms", quantityKey: "upToFive" },
    ],
  },
  {
    id: "unlimited",
    features: [
      { labelKey: "unlimitedLinks" },
      { labelKey: "unlimitedForms" },
      { labelKey: "noCeiling" },
    ],
  },
];

const FAQ_KEYS = ["usage", "exceed", "change", "links", "starterLimits", "cancel"] as const;

interface PricingSectionProps {
  variant?: "landing" | "trialExpired";
}

export function PricingSection({ variant = "landing" }: PricingSectionProps) {
  const { t } = useLanguage();

  return (
    <section id="pricing" className="bg-muted/50 py-12 sm:py-16 lg:py-20">
      <div className="max-w-6xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-3 mb-10 sm:mb-12">
          <p className="text-xs font-bold text-primary uppercase tracking-widest">
            {t("landing.pricing.label")}
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground tracking-tight">
            {t("landing.pricing.headline")}
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">
            {t("landing.pricing.subheadline")}
          </p>
        </div>

        {/* Tier Cards - M3 Style */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-10 sm:mb-12">
          {TIER_CONFIGS.map((tier) => (
            <Card
              key={tier.id}
              className={`relative flex flex-col overflow-hidden transition-all duration-200 hover:shadow-lg ${
                tier.isRecommended
                  ? "border-primary/60 ring-1 ring-primary/20 bg-card"
                  : "border-border/50 bg-card/80"
              }`}
            >
              {/* Recommended Badge */}
              {tier.isRecommended && (
                <div className="absolute top-0 right-0">
                  <Badge
                    variant="default"
                    className="rounded-none rounded-bl-lg px-3 py-1 text-xs font-medium bg-primary text-primary-foreground"
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    {t(`landing.pricing.${tier.id}.badge`)}
                  </Badge>
                </div>
              )}

              <CardHeader className="pb-4 pt-6">
                {/* Price */}
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl sm:text-5xl font-bold text-foreground">
                    {t(`landing.pricing.${tier.id}.price`)}
                  </span>
                  <span className="text-lg text-muted-foreground">
                    {t("landing.pricing.currency")}
                  </span>
                  <span className="text-sm text-muted-foreground ml-1">
                    {t("landing.pricing.period")}
                  </span>
                </div>

                {/* Tier Name */}
                <h3 className="text-lg font-semibold text-foreground mt-2">
                  {t(`landing.pricing.${tier.id}.name`)}
                </h3>

                {/* Tagline */}
                <p className="text-sm text-muted-foreground">
                  {t(`landing.pricing.${tier.id}.tagline`)}
                </p>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col pt-0">
                {/* Description */}
                <p className="text-sm text-foreground/80 mb-4">
                  {t(`landing.pricing.${tier.id}.description`)}
                </p>

                {/* Feature List */}
                <ul className="space-y-2.5 flex-1">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground/90">
                        {feature.quantityKey && (
                          <span className="font-medium">
                            {t(`landing.pricing.quantity.${feature.quantityKey}`)}{" "}
                          </span>
                        )}
                        {t(`landing.pricing.features.${feature.labelKey}`)}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Capacity Line */}
                <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border/30">
                  {t(`landing.pricing.${tier.id}.capacity`)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Builder Section */}
        <div className="bg-card/60 backdrop-blur-sm rounded-xl border border-border/30 p-6 sm:p-8 mb-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-primary/10">
              <Info className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">
              {t("landing.pricing.trust.headline")}
            </h3>
          </div>
          <ul className="space-y-2 ml-11">
            <li className="flex items-center gap-2 text-sm text-foreground/80">
              <span className="text-primary">•</span>
              {t("landing.pricing.trust.point1")}
            </li>
            <li className="flex items-center gap-2 text-sm text-foreground/80">
              <span className="text-primary">•</span>
              {t("landing.pricing.trust.point2")}
            </li>
            <li className="flex items-center gap-2 text-sm text-foreground/80">
              <span className="text-primary">•</span>
              {t("landing.pricing.trust.point3")}
            </li>
          </ul>
        </div>

        {/* Emotional Tagline */}
        <p className="text-center text-sm sm:text-base text-muted-foreground italic mb-10">
          "{t("landing.pricing.tagline")}"
        </p>

        {/* FAQ Accordion */}
        <div className="max-w-2xl mx-auto">
          <h3 className="text-lg font-semibold text-foreground mb-4 text-center">
            {t("landing.pricing.faq.label")}
          </h3>
          <Accordion type="single" collapsible className="w-full">
            {FAQ_KEYS.map((key) => (
              <AccordionItem key={key} value={key} className="border-border/50">
                <AccordionTrigger className="text-sm text-left hover:no-underline py-4">
                  {t(`landing.pricing.faq.items.${key}.q`)}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground pb-4">
                  {t(`landing.pricing.faq.items.${key}.a`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
